"use client";

import { useEffect, useRef } from "react";
import Link from "next/link";
import Image from "next/image";
import { Button } from "@/components/ui/button";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useTranslation } from "@/hooks/useTranslation";
import { useParams } from "next/navigation";
import { Locale, isRTLLanguage } from "@/lib/i18n/config";
const logos = [
  { src: "/images/logos/health-logo.webp", tooltipKey: "hero.badges.ministry", round: false },
  { src: "/images/logos/ktb.webp", tooltipKey: "hero.badges.turizim", round: false },
  { src: "/images/logos/IFIA.webp", tooltipKey: "hero.badges.ifia", round: true },
  { src: "/images/logos/iso.webp", tooltipKey: "hero.badges.iso", round: false },
];

export default function HeroSection() {
  const { t, locale } = useTranslation("home");
  const params = useParams();
  const currentLocale = (params?.locale as Locale) || locale;
  const isRTL = isRTLLanguage(currentLocale);
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const timeUpdateHandlerRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (typeof window !== "undefined") {
      gsap.registerPlugin(ScrollTrigger);

      const video = videoRef.current;
      if (video) {
        const setupVideoLoop = () => {
          if (!video) return;
          const duration = video.duration;

          if (timeUpdateHandlerRef.current) {
            video.removeEventListener(
              "timeupdate",
              timeUpdateHandlerRef.current
            );
            timeUpdateHandlerRef.current = null;
          }

          if (isFinite(duration) && duration > 0) {
            let loopAtTime = duration - 5.0;

            if (loopAtTime < 0 || duration <= 5.0) {
              loopAtTime = Math.max(0, duration - 0.2);
            }

            const newTimeUpdateHandler = () => {
              if (
                video &&
                video.currentTime >= loopAtTime &&
                video.duration > 0
              ) {
                video.currentTime = 0;
                video
                  .play()
                  .catch((error) =>
                    console.error("Error re-playing video:", error)
                  );
              }
            };

            video.addEventListener("timeupdate", newTimeUpdateHandler);
            timeUpdateHandlerRef.current = newTimeUpdateHandler;
          }
        };

        video.addEventListener("loadedmetadata", setupVideoLoop);
        video.addEventListener("durationchange", setupVideoLoop);

        if (video.readyState >= 1) {
          setupVideoLoop();
        }
      }
    }

    return () => {
      const video = videoRef.current;
      if (video && timeUpdateHandlerRef.current) {
        video.removeEventListener("timeupdate", timeUpdateHandlerRef.current);
      }
    };
  }, []);

  return (
    <div ref={containerRef} className="relative h-screen min-h-[600px] overflow-hidden">
      {/* Video background */}
      <div className="absolute inset-0 w-full h-full">
        <video
          ref={videoRef}
          className="w-full h-full object-cover"
          autoPlay
          muted
          playsInline
          aria-hidden="true"
          poster="/videos/env-old-poster.jpg"
          preload="auto"
        >
          <source src="/videos/env-old-720.mp4" type="video/mp4" />
        </video>
        {/* Gradient overlay — heavier on the content side for readability */}
        <div
          className={`absolute inset-0 ${
            isRTL
              ? "bg-gradient-to-l from-[#0A1D44]/80 via-[#0A1D44]/60 to-[#222D52]/30"
              : "bg-gradient-to-r from-[#0A1D44]/80 via-[#0A1D44]/60 to-[#222D52]/30"
          }`}
        />
      </div>

      {/* Gold vertical accent line */}
      <div
        className={`absolute top-0 bottom-0 w-px bg-[#D2A138]/20 z-10 ${
          isRTL ? "right-8 lg:right-16" : "left-8 lg:left-16"
        }`}
      />

      {/* Main layout grid */}
      <div className="relative z-10 h-full flex items-center">
        <div className="w-full flex items-center justify-between">

          {/* Left / RTL-right content block */}
          <div
            className={`flex flex-col justify-center text-white w-full lg:w-3/5 xl:w-1/2 mt-16 ${
              isRTL
                ? "items-end text-right pr-12 lg:pr-24 pl-6 lg:pl-12"
                : "items-start text-left pl-12 lg:pl-24 pr-6 lg:pr-12"
            }`}
          >
            {/* Main headline — rendered as plain h1 (not motion.h1) so it paints
                immediately on SSR without waiting for framer-motion hydration.
                This is the LCP element; animating its opacity was adding ~10s
                to mobile LCP. */}
            <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-tight mb-5">
              {/* Render title with gold accent on last word (LTR) or first word (RTL) */}
              {(() => {
                const title: string = t("hero.title");
                const words = title.split(" ");
                if (words.length <= 1) return <span>{title}</span>;
                if (isRTL) {
                  const first = words.shift();
                  return (
                    <>
                      <span className="text-[#D2A138]">{first}</span>{" "}
                      {words.join(" ")}
                    </>
                  );
                }
                const last = words.pop();
                return (
                  <>
                    {words.join(" ")}{" "}
                    <span className="text-[#D2A138]">{last}</span>
                  </>
                );
              })()}
            </h1>

            {/* Subtitle — plain <p> (no framer opacity animation) so it paints
                immediately on SSR without waiting for hydration. */}
            <p className="text-base sm:text-lg md:text-xl text-white/80 max-w-lg mb-8 leading-relaxed">
              {t("hero.subtitle")}
            </p>

            {/* CTA buttons — plain div (no framer animation) */}
            <div className={`flex flex-col sm:flex-row gap-3 mb-12 ${isRTL ? "sm:flex-row-reverse" : ""}`}>
              <Link href={`/${currentLocale}/treatments`}>
                <Button
                  size="lg"
                  className={`bg-[#D2A138] hover:bg-[#b8882e] text-[#0A1D44] font-semibold px-6 flex items-center gap-2 ${
                    isRTL ? "flex-row-reverse" : ""
                  }`}
                >
                  {t("hero.cta.exploreTreatments")}
                  {isRTL ? (
                    <ArrowLeft className="h-4 w-4 shrink-0" />
                  ) : (
                    <ArrowRight className="h-4 w-4 shrink-0" />
                  )}
                </Button>
              </Link>

              <Link href={`/${currentLocale}/book-appointment`}>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-white/60 text-white hover:bg-white/10 hover:border-white px-6"
                >
                  {t("hero.cta.bookConsultation")}
                </Button>
              </Link>
            </div>

            {/* Recognised Excellence — plain div */}
            <div>
              <p className="text-xs uppercase tracking-widest text-[#D2A138]/80 font-semibold mb-3">
                {t("hero.badges.title")}
              </p>
              <div className={`flex items-center gap-5 sm:gap-7 ${isRTL ? "flex-row-reverse" : ""}`}>
                {logos.map((logo, index) =>
                  logo.round ? (
                    <div
                      key={index}
                      title={t(logo.tooltipKey)}
                      className="rounded-full overflow-hidden bg-white flex items-center justify-center h-9 w-9 sm:h-10 sm:w-10 shrink-0 cursor-default"
                    >
                      <Image
                        src={logo.src}
                        alt={t(logo.tooltipKey)}
                        width={40}
                        height={40}
                        sizes="40px"
                        className="h-7 w-7 sm:h-8 sm:w-8 object-contain"
                      />
                    </div>
                  ) : (
                    <div key={index} title={t(logo.tooltipKey)} className="cursor-default">
                      <Image
                        src={logo.src}
                        alt={t(logo.tooltipKey)}
                        width={80}
                        height={32}
                        sizes="80px"
                        className="h-9 sm:h-10 w-auto object-contain"
                      />
                    </div>
                  )
                )}
              </div>
            </div>
          </div>

          {/* Right decorative element — desktop only. Plain div (no framer)
              so it paints immediately; visual is static anyway. */}
          <div
            className={`hidden lg:flex flex-col items-center gap-3 pr-12 xl:pr-20 shrink-0 ${
              isRTL ? "lg:hidden" : ""
            }`}
            aria-hidden="true"
          >
            {/* Gold gradient line above */}
            <div className="h-16 w-px bg-gradient-to-b from-transparent to-[#D2A138]/60" />
            {/* Vertical text */}
            <p
              className="text-xs font-semibold tracking-[0.3em] uppercase text-white/50"
              style={{ writingMode: "vertical-rl", textOrientation: "mixed" }}
            >
              {t("hero.premiumDentalCare")}
            </p>
            {/* Gold gradient line below */}
            <div className="h-16 w-px bg-gradient-to-b from-[#D2A138]/60 to-transparent" />
          </div>

        </div>
      </div>
    </div>
  );
}
